'use client';

import { BrowserRecordingArtifactMetadataSchema } from '@terra/shared-contracts-artifact';
import { Cancel01Icon, PlayIcon } from '@terra/ui-icons';
import { HStack, VStack, css } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '../../../../components/button';
import { LegacyDialog } from '../../../../components/dialog';
import { IconButton } from '../../../../components/icon-button';
import { Spinner } from '../../../../components/spinner';
import { HeadingModal, MetaDefault } from '../../../../components/typography';
import { useBrowserRecordingUrl } from '../hooks/useBrowserRecordingUrl';
import { useRecordingArtifact } from '../hooks/useRecordingArtifact';

const RECORDING_PLAYBACK_RATE = 2;

const recordingFrameStyle = css({
  display: 'block',
  width: '100%',
  maxHeight: '70vh',
  aspectRatio: '16 / 9',
  objectFit: 'contain',
  borderRadius: 'md',
  bg: 'bg.muted',
});

type RecordingDialogButtonProps = {
  toolCallId: string;
};

export function RecordingDialogButton({
  toolCallId,
}: RecordingDialogButtonProps) {
  const t = useTranslations('designSystem.chat.browserResult');
  const tCommon = useTranslations('designSystem.common');
  const [isOpen, setIsOpen] = useState(false);
  const artifact = useRecordingArtifact(toolCallId);
  const parsedMetadata = BrowserRecordingArtifactMetadataSchema.safeParse(
    artifact?.metadata,
  );
  const { recordingUrl, isLoading } = useBrowserRecordingUrl({
    artifactId: parsedMetadata.success && isOpen ? artifact?.id : undefined,
    artifactCreatedAt: artifact?.createdAt,
  });

  if (!parsedMetadata.success) {
    return null;
  }

  return (
    <>
      <Button variant="ghost" size="xs" onClick={() => setIsOpen(true)}>
        <PlayIcon size={12} />
        {t('recordingAction')}
      </Button>

      <LegacyDialog.Root
        open={isOpen}
        onOpenChange={({ open }) => setIsOpen(open)}
        lazyMount
        unmountOnExit
      >
        <LegacyDialog.Backdrop />
        <LegacyDialog.Positioner>
          <LegacyDialog.Content maxW="4xl" w="full">
            <VStack gap={4} p={6} alignItems="stretch">
              <HStack justify="space-between" alignItems="center" gap={4}>
                <LegacyDialog.Title asChild>
                  <HeadingModal>{t('recording')}</HeadingModal>
                </LegacyDialog.Title>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  aria-label={tCommon('close')}
                >
                  <Cancel01Icon size={16} />
                </IconButton>
              </HStack>

              {recordingUrl ? (
                <video
                  className={recordingFrameStyle}
                  src={recordingUrl.signedUrl}
                  poster={parsedMetadata.data.recordingThumbnail}
                  controls
                  autoPlay
                  onLoadedMetadata={(event) => {
                    event.currentTarget.playbackRate = RECORDING_PLAYBACK_RATE;
                  }}
                  aria-label={t('recording')}
                />
              ) : (
                <HStack justify="center" py={10}>
                  {isLoading ? (
                    <Spinner size="md" />
                  ) : (
                    <MetaDefault color="fg.muted">
                      {t('recordingUnavailable')}
                    </MetaDefault>
                  )}
                </HStack>
              )}
            </VStack>
          </LegacyDialog.Content>
        </LegacyDialog.Positioner>
      </LegacyDialog.Root>
    </>
  );
}
